import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BranchCode } from "api/risk";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { useRefreshSubscriptionProcess } from "../hooks/useRefreshSubscriptionProcess";
import { CopropertySurvey, SurveyStepControls } from "./CopropertySurvey";
import { useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/** Navigation de route (retour à la copropriété, suite vers les bâtiments), hors blocs. */
const ROUTE_STEP_CONFIG: BranchStepConfig = {
  [BranchCode.Mri]: createStepConfig({
    prev: {
      identifier: MriStepIdentifier.CopropertyRegistration,
      route: MRI_ROUTES.copropertyRegistration,
      label: "Retour à la copropriété",
    },
    next: {
      identifier: MriStepIdentifier.CopropertyBuildings,
      route: MRI_ROUTES.copropertyBuildings,
      label: "Aller aux bâtiments",
    },
  }),
};

/**
 * Sous-étape « questionnaire copropriété » de la création du dossier MRI : les blocs
 * COPRO_MRI (appréciation du risque, kill questions), rendus bloc par bloc avec le rail
 * de progression et la fiche copropriété. Les boutons d'étape de la page font avancer
 * les blocs (le questionnaire remonte libellés et actions). Requiert une copropriété
 * rattachée — sinon retour explicite (toast) à la sous-étape de sélection.
 * La boucle bâtiments suit (le bloc environnement, le plus éliminatoire, est posé avant
 * la saisie des bâtiments).
 */
export const CopropertySurveyPage = () => {
  const { riskId, copropertyId, coproperty, isLoading } = useCoproperty();
  const { goToNext: goToBuildings, goToPrevious: goToCoproperty } = useStepNavigation(ROUTE_STEP_CONFIG);
  const { refresh } = useRefreshSubscriptionProcess();
  const navigate = useNavigate();

  /** Boutons d'étape du bloc courant, remontés par le questionnaire. */
  const [controls, setControls] = useState<SurveyStepControls | null>(null);
  const handleControlsChange = useCallback((next: SurveyStepControls) => setControls(next), []);

  // Session terminée : le process refait le point (sous-étape complétée) et le stepper
  // avance vers la boucle bâtiments. Mémorisé : une fonction recréée à chaque rendu se
  // propage jusqu'à l'effet du questionnaire qui remonte les boutons, et boucle avec setControls.
  const handleCompleted = useCallback(async () => {
    await refresh();
    goToBuildings();
  }, [refresh, goToBuildings]);

  const blockStepConfig = useMemo<BranchStepConfig>(
    () => ({
      [BranchCode.Mri]: createStepConfig({
        prev: {
          identifier: MriStepIdentifier.CopropertyRegistration,
          route: MRI_ROUTES.copropertyRegistration,
          label: controls?.prevLabel ?? "Retour à la copropriété",
          onClick: controls?.onPrevious ?? goToCoproperty,
        },
        next: {
          identifier: MriStepIdentifier.CopropertyBuildings,
          route: MRI_ROUTES.copropertyBuildings,
          label: controls?.nextLabel ?? "Bloc suivant",
          disabled: controls?.nextDisabled ?? true,
          onClick: controls?.onNext,
        },
      }),
    }),
    [controls, goToCoproperty]
  );
  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(blockStepConfig);

  const hasCoproperty = copropertyId != null;

  // Pas de copropriété rattachée : on explique le retour au lieu de rediriger en silence
  // (l'utilisateur avait l'impression de « rester sur la même page »).
  useEffect(() => {
    if (!riskId || hasCoproperty) return;

    toast.warn("Rattachez d'abord une copropriété pour accéder au questionnaire.");
    navigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`, { replace: true });
  }, [riskId, hasCoproperty, navigate]);

  if (!hasCoproperty) {
    return <GenericLoading isPageCentered />;
  }

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        {isLoading || !coproperty ? (
          <GenericLoading isPageCentered />
        ) : (
          <CopropertySurvey
            coproperty={coproperty}
            surveyRoute={`${MRI_ROUTES.copropertySurvey}/${riskId}`}
            onExitBack={goToCoproperty}
            onControlsChange={handleControlsChange}
            onCompleted={handleCompleted}
          />
        )}
      </StepPageWrapper>
    </div>
  );
};
